import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Loader2, AlertCircle, KeyRound } from 'lucide-react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { signIn, completeNewPasswordChallenge, getCurrentSession } from '../lib/cognito';
import { useAuthStore } from '../store/authStore';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setCognitoSession);
  // Where to land after sign-in — agents go to their portal, admins to the
  // admin workspace. Read after setSession() so the role is resolved.
  const homeFor = () =>
    useAuthStore.getState().user?.role === 'agent' ? '/my/dashboard' : '/cycle';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<{
    user: CognitoUser;
    userAttributes: any;
  } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  // If already signed in, redirect immediately
  useEffect(() => {
    getCurrentSession().then((s) => {
      if (s) {
        setSession(s);
        const dest = (location.state as { from?: string } | null)?.from || homeFor();
        navigate(dest, { replace: true });
      }
    });
  }, [navigate, location.state, setSession]);

  const onSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await signIn(email.trim(), password);
      if (r.kind === 'success') {
        setSession(r.session);
        navigate(homeFor(), { replace: true });
      } else {
        setChallenge({ user: r.user, userAttributes: r.userAttributes });
        setPassword('');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    if (newPassword !== newPasswordConfirm) {
      setError("Passwords don't match");
      return;
    }
    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await completeNewPasswordChallenge(
        challenge.user,
        newPassword,
        challenge.userAttributes,
      );
      setSession(session);
      navigate(homeFor(), { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-shell min-h-screen flex items-center justify-center p-6">
      <div className="login-bg" />
      <div className="login-glow-tl" />
      <div className="login-glow-br" />

      <div className="login-card rounded-2xl p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png" alt="" className="h-10 w-auto" />
          <div>
            <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
              Empire ACA Pay
            </h1>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {challenge ? 'Set a new password' : 'Sign in'}
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-error flex items-start gap-2 mb-4">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <p className="text-xs">{error}</p>
          </div>
        )}

        {challenge ? (
          <form onSubmit={onSetNewPassword} className="space-y-4">
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              First sign-in: choose a permanent password (min 12 chars, mix of upper/lower/digits/symbols).
            </p>
            <div>
              <label className="label">New password</label>
              <input
                type="password"
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div>
              <label className="label">Confirm new password</label>
              <input
                type="password"
                className="input"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              {busy ? 'Setting…' : 'Set password & sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={onSignIn} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </div>
            <div>
              <label className="label">Password</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
